import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
// CODEX_PROMPT_86 (Bug A) : titre dynamique base sur le mois du defi actif
import { useMonthlyChallenge } from '../hooks/useMonthlyChallenge';
// CODEX_PROMPT_88 : contenu dynamique (settings + cartes) pilote par l'admin.
import { useWhatsNew } from '../hooks/useWhatsNew';
import { resolveWhatsNewIcon } from '../data/whatsNewIcons';
import {
  WHATS_NEW_VISIBLE,
  WHATS_NEW_STARTED,
  WHATS_NEW_BADGE_DAYS,
  WHATS_NEW_HEADER,
  WHATS_NEW_ITEMS,
} from '../data/whatsNew';

// Calcule la visibilite du badge "Nouveau" a partir d'une date de debut et d'une
// duree en jours. CODEX_PROMPT_88 : parametre (auparavant lisait les constantes
// de module) pour piloter la fenetre depuis whats_new_settings.
function isNewBadgeVisible(startedOn, badgeDays) {
  if (!startedOn) return false;
  const start = new Date(startedOn);
  if (Number.isNaN(start.getTime())) return false;
  const now = new Date();
  const diffDays = (now - start) / (1000 * 60 * 60 * 24);
  const days = Number.isFinite(Number(badgeDays)) ? Number(badgeDays) : WHATS_NEW_BADGE_DAYS;
  return diffDays >= 0 && diffDays <= days;
}

// Resout un champ bilingue { fr, en } selon la langue. Tolere les chaines
// simples (retro-compat) en les renvoyant telles quelles.
function pickText(field, isFr) {
  if (field && typeof field === 'object') {
    return isFr ? (field.fr ?? field.en) : (field.en ?? field.fr);
  }
  return field;
}

// CODEX_PROMPT_88 : construit la carte "Defi du mois" auto depuis le hook defi.
// Toujours premiere, non editable, a jour a chaque rotation.
function buildAutoChallengeCard(challenge) {
  const sparkles = resolveWhatsNewIcon('sparkles');
  const revealed = Boolean(challenge?.isRevealed);
  return {
    id: 'auto-defi-mois',
    title: revealed
      ? { fr: 'Défi du mois révélé !', en: 'Monthly challenge revealed!' }
      : { fr: 'Un défi mystère arrive…', en: 'A mystery challenge is coming…' },
    // Description masquee dans l'UI (voir WhatsNewCard) mais transmise quand meme.
    description: revealed
      ? {
          fr: `Découvre ${challenge?.title?.fr || ''}`.trim(),
          en: `Discover ${challenge?.title?.en || ''}`.trim(),
        }
      : { fr: '', en: '' },
    icon: sparkles.icon,
    iconBg: sparkles.iconBg,
    iconColor: sparkles.iconColor,
    route: '/wheel',
    pulse: true,
  };
}

// CODEX_PROMPT_88 : mappe une ligne DB whats_new_cards vers le format WhatsNewCard.
function cardRowToItem(row) {
  const resolved = resolveWhatsNewIcon(row.icon_key);
  return {
    id: row.id,
    title: { fr: row.title_fr, en: row.title_en },
    description: { fr: row.description_fr || '', en: row.description_en || '' },
    icon: resolved.icon,
    iconBg: resolved.iconBg,
    iconColor: resolved.iconColor,
    route: row.route,
    pulse: Boolean(row.pulse),
    cta: Boolean(row.is_cta),
  };
}

function WhatsNewCard({ item, index, reduceMotion, language = 'fr' }) {
  const navigate = useNavigate();
  const isFr = language === 'fr';
  const Icon = item.icon;
  const itemTitle = pickText(item.title, isFr);

  return (
    <motion.button
      type="button"
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.25, delay: 0.1 + index * 0.07, ease: [0.4, 0, 0.2, 1] }
      }
      whileTap={reduceMotion ? undefined : { scale: 0.97 }}
      onClick={() => navigate(item.route)}
      className={`w-full flex items-center gap-2.5 rounded-[16px] py-2 px-2.5 text-left ${
        item.cta ? '' : 'bg-white'
      }`}
      style={{
        border: item.cta
          ? '1.5px solid var(--color-primary-light)'
          : '1px solid var(--cb-border)',
        backgroundColor: item.cta ? 'var(--color-primary-soft)' : undefined,
        minHeight: '44px',
      }}
      aria-label={`${isFr ? 'Ouvrir' : 'Open'} ${itemTitle}`}
    >
      <div className="relative flex-shrink-0">
        {item.pulse && !reduceMotion && (
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-[11px] pointer-events-none"
            style={{ backgroundColor: item.iconColor }}
            animate={{ opacity: [0.35, 0, 0.35], scale: [1, 1.35, 1] }}
            transition={{ duration: 2.2, ease: 'easeInOut', repeat: Infinity }}
          />
        )}
        <div
          className="relative flex items-center justify-center rounded-[11px]"
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: item.iconBg,
          }}
        >
          <Icon size={19} style={{ color: item.iconColor }} strokeWidth={2} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p
          className="text-[14px] font-bold leading-tight"
          style={{ color: item.cta ? 'var(--color-primary-dark)' : 'var(--color-text-primary)' }}
        >
          {itemTitle}
        </p>
        {/* Description cachee pour un rendu compact (PROMPT_55 v2).
            Conservee derriere {false && ...} pour reactivation rapide. */}
        {false && (
          <p
            className="text-[12px] leading-snug mt-0.5"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {pickText(item.description, isFr)}
          </p>
        )}
      </div>

      <ChevronRight
        size={18}
        className="flex-shrink-0"
        style={{ color: 'var(--color-primary)' }}
      />
    </motion.button>
  );
}

export default function WhatsNewSection({ language = 'fr' }) {
  const reduceMotion = useReducedMotion();
  const isFr = language === 'fr';
  // CODEX_PROMPT_86 (Bug A) : titre "<Mois> avec Bongo" derive du defi actif (hook).
  // Fallback sur WHATS_NEW_HEADER.title si le challenge n'est pas encore resolu
  // (loading) ou sans monthLabel. Le hook a un cache 5 min -> pas de cout reseau.
  const { challenge } = useMonthlyChallenge();
  // CODEX_PROMPT_88 : contenu dynamique. content === null => fallback statique.
  const { content } = useWhatsNew();

  const monthNameFr = challenge?.monthLabel?.fr?.split(' ')[0];
  const monthNameEn = challenge?.monthLabel?.en?.split(' ')[0];
  const dynamicTitle =
    monthNameFr && monthNameEn
      ? { fr: `${monthNameFr} avec Bongo`, en: `${monthNameEn} with Bongo` }
      : WHATS_NEW_HEADER.title;

  // ---- Resolution du contenu : dynamique (Supabase) ou fallback statique ----
  let items;
  let description;
  let badgeStartedOn;
  let badgeDays;

  if (content) {
    const settings = content.settings;
    // Visibilite pilotee par l'admin.
    if (settings && settings.section_visible === false) {
      return null;
    }
    // Carte defi auto (toujours premiere) + cartes manuelles publiees.
    items = [buildAutoChallengeCard(challenge), ...content.cards.map(cardRowToItem)];
    description = settings
      ? { fr: settings.description_fr || '', en: settings.description_en || '' }
      : null;
    badgeStartedOn = settings?.badge_started_on || null;
    badgeDays = settings?.badge_days;
  } else {
    // Fallback reseau : comportement 100 % identique a l'ancien (constantes
    // statiques, y compris la carte defi hardcodee -> pas de carte auto ici).
    if (!WHATS_NEW_VISIBLE || !WHATS_NEW_ITEMS || WHATS_NEW_ITEMS.length === 0) {
      return null;
    }
    items = WHATS_NEW_ITEMS;
    description = WHATS_NEW_HEADER.description;
    badgeStartedOn = WHATS_NEW_STARTED;
    badgeDays = WHATS_NEW_BADGE_DAYS;
  }

  if (!items || items.length === 0) {
    return null;
  }

  const showBadge = isNewBadgeVisible(badgeStartedOn, badgeDays);

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="relative rounded-[20px] p-4 mb-5"
      style={{
        backgroundColor: 'var(--color-surface-soft)',
        border: showBadge
          ? '2px solid var(--color-primary)'
          : '1px solid var(--cb-border)',
        boxShadow: 'var(--shadow-xs)',
      }}
      aria-label={isFr ? 'Nouveautes' : "What's new"}
    >
      {showBadge && (
        <span
          className="absolute -top-2.5 right-4 text-[10px] font-bold uppercase tracking-wider text-white px-2.5 py-0.5 rounded-full"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {isFr ? 'Nouveau' : 'New'}
        </span>
      )}

      <p
        className="text-[11px] font-bold uppercase mb-1"
        style={{
          color: 'var(--color-text-tertiary)',
          letterSpacing: '0.07em',
        }}
      >
        {pickText(WHATS_NEW_HEADER.overline, isFr)}
      </p>

      <h2
        className="text-[17px] font-extrabold leading-tight"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {pickText(dynamicTitle, isFr)}
      </h2>

      {description && pickText(description, isFr) && (
        <p
          className="text-[13px] mt-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {pickText(description, isFr)}
        </p>
      )}

      <div className="flex flex-col gap-1.5 mt-2.5">
        {items.map((item, index) => (
          <WhatsNewCard
            key={item.id}
            item={item}
            index={index}
            reduceMotion={reduceMotion}
            language={language}
          />
        ))}
      </div>
    </motion.section>
  );
}
